import React, { useEffect, useState } from 'react';
import { motion } from 'framer-motion';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '@/contexts/AuthContext';
import { chatAPI } from '@/services/api';
import { Button } from '@/components/ui/button';
import { ArrowLeft, MessageCircle, Search } from 'lucide-react';
import { toast } from 'sonner';

const ConversationsPage = () => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [conversations, setConversations] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');

  useEffect(() => {
    fetchConversations();
  }, []);

  const fetchConversations = async () => {
    try {
      const data = await chatAPI.getConversations();
      setConversations(data);
    } catch (error) {
      toast.error('Falha ao carregar conversas');
    } finally {
      setLoading(false);
    }
  };

  const filteredConversations = conversations.filter(conv => {
    const title = conv.scenario 
      ? `${conv.scenario} prática` 
      : conv.title;
    return title.toLowerCase().includes(searchTerm.toLowerCase());
  });

  if (loading) {
    return (
      <div className="min-h-screen bg-sand flex items-center justify-center">
        <div className="animate-pulse text-forest text-lg">Carregando conversas...</div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-sand">
      <header className="bg-white border-b border-forest/10 p-4">
        <div className="container mx-auto flex items-center gap-4">
          <Button
            onClick={() => navigate('/dashboard')}
            variant="ghost"
            data-testid="back-to-dashboard-btn"
          >
            <ArrowLeft className="w-5 h-5" />
          </Button>
          <h1 className="font-fraunces text-2xl font-semibold text-forest">
            Todas as Conversas
          </h1>
        </div>
      </header>

      <div className="container mx-auto px-6 md:px-12 py-12 max-w-4xl">
        {/* Search */}
        <div className="mb-8">
          <div className="relative">
            <Search className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-stone" />
            <input
              type="text"
              placeholder="Buscar conversas..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="w-full pl-12 pr-4 py-4 rounded-2xl border border-forest/10 bg-white focus:ring-2 focus:ring-lime/50 focus:border-lime"
              data-testid="search-conversations"
            />
          </div>
        </div>

        {/* Conversations List */}
        {filteredConversations.length === 0 ? (
          <div className="bg-white rounded-3xl p-12 text-center shadow-soft">
            <MessageCircle className="w-16 h-16 text-stone/30 mx-auto mb-4" />
            <p className="text-stone">
              {searchTerm ? 'Nenhuma conversa encontrada' : 'Nenhuma conversa ainda'}
            </p>
            {!searchTerm && (
              <Button
                onClick={() => navigate('/dashboard')}
                className="mt-4 bg-forest text-sand hover:bg-forest/90 rounded-full"
              >
                Iniciar Primeira Conversa
              </Button>
            )}
          </div>
        ) : (
          <div className="space-y-3">
            {filteredConversations.map((conv, index) => (
              <motion.div
                key={conv.id}
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: index * 0.05 }}
                whileHover={{ scale: 1.01 }}
                onClick={() => navigate(`/chat/${conv.id}`)}
                className="bg-white rounded-2xl p-6 shadow-soft cursor-pointer hover:shadow-medium hover:border-lime/50 border-2 border-transparent transition-all"
                data-testid={`conversation-item-${conv.id}`}
              >
                <div className="flex items-start justify-between gap-4">
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 mb-2">
                      <h3 className="font-medium text-forest text-lg">
                        {conv.scenario 
                          ? `Prática de ${conv.scenario.charAt(0).toUpperCase()}${conv.scenario.slice(1).replace('_', ' ')}`
                          : conv.title}
                      </h3>
                      {conv.mode === 'scenario' && (
                        <span className="px-2 py-1 bg-lime/20 text-forest text-xs rounded-full font-medium">
                          Lição
                        </span>
                      )}
                    </div>
                    <p className="text-sm text-stone mb-1">
                      {new Date(conv.updated_at).toLocaleDateString('pt-BR', {
                        weekday: 'short',
                        month: 'short',
                        day: 'numeric',
                        year: 'numeric',
                        hour: '2-digit',
                        minute: '2-digit'
                      })}
                    </p>
                    <p className="text-xs text-stone/60">
                      Criada em {new Date(conv.created_at).toLocaleDateString('pt-BR', {
                        month: 'short',
                        day: 'numeric',
                        year: 'numeric'
                      })}
                    </p>
                  </div>
                  <MessageCircle className="w-6 h-6 text-stone hover:text-lime transition-colors flex-shrink-0" />
                </div>
              </motion.div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

export default ConversationsPage;
